import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "tamore_cart_v1";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
      return Array.isArray(stored) ? stored : [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((product, size, quantity = 1) => {
    setItems((prev) => {
      const idx = prev.findIndex((i) => i.product_id === product.id && i.size === size);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: Math.min(10, next[idx].quantity + quantity) };
        return next;
      }
      return [
        ...prev,
        {
          product_id: product.id,
          slug: product.slug,
          name: product.name,
          price: product.price,
          currency: product.currency || "INR",
          image: product.images?.[0] || "",
          size,
          quantity,
        },
      ];
    });
    setOpen(true);
  }, []);

  const removeItem = useCallback((product_id, size) => {
    setItems((prev) => prev.filter((i) => !(i.product_id === product_id && i.size === size)));
  }, []);

  const updateQty = useCallback((product_id, size, quantity) => {
    setItems((prev) =>
      prev.map((i) =>
        i.product_id === product_id && i.size === size
          ? { ...i, quantity: Math.max(1, Math.min(10, quantity)) }
          : i
      )
    );
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const subtotal = useMemo(
    () => items.reduce((sum, i) => sum + i.price * i.quantity, 0),
    [items]
  );
  const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);

  return (
    <CartContext.Provider
      value={{ items, open, setOpen, addItem, removeItem, updateQty, clear, subtotal, count }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
